import type {
  ApolloClient,
  DefaultContext,
  FetchResult,
  MutationOptions,
  OperationVariables,
} from '@apollo/client';
import { getOperationName } from '@apollo/client/utilities';

import { isEffectivelyOnline } from './connectivity';
import { OFFLINE_QUEUE_TIMEOUT_MS } from './OfflineQueueLink';

/**
 * Plazo de cada paso de la subida de fotos que va por Apollo (pedir el permiso
 * de subida y dar de alta la foto). El mismo techo que el resto de guardados:
 * el backend corta su transacción a los 20 s y 25 s deja margen a una respuesta
 * lenta pero viva. La subida del fichero NO pasa por aquí: tiene su propio
 * detector de estancamiento por bytes (`uploadWithProgress`).
 */
export const UPLOAD_STEP_TIMEOUT_MS = OFFLINE_QUEUE_TIMEOUT_MS;

/**
 * La mutation no contestó a tiempo. Sin `httpStatus` ni errores GraphQL, así
 * que la cola la trata como fallo de red: transitorio, se reintenta y la foto
 * no se descarta (`classifyOfflineError` → `transient`, `withRetry` no la da
 * por terminal).
 */
export class MutationDeadlineError extends Error {
  readonly operationName: string;

  readonly timeoutMs: number;

  constructor(operationName: string, timeoutMs: number) {
    super(`${operationName} sin respuesta en ${timeoutMs} ms`);
    this.name = 'MutationDeadlineError';
    this.operationName = operationName;
    this.timeoutMs = timeoutMs;
  }
}

/**
 * El sondeo ya sabe que el backend no contesta: se falla sin salir a la red.
 * Es lo que hacía el `OfflineQueueLink` con estas llamadas antes de saltárselo
 * (`skipOfflineQueue`); sin esto, cada intento esperaría el plazo entero.
 * Transitorio, igual que `MutationDeadlineError`.
 */
export class BackendUnreachableError extends Error {
  readonly operationName: string;

  constructor(operationName: string) {
    super(`${operationName} sin enviar: el backend no contesta`);
    this.name = 'BackendUnreachableError';
    this.operationName = operationName;
  }
}

type MutatingClient = Pick<ApolloClient<unknown>, 'mutate'>;

/**
 * `client.mutate` con plazo, para quien gestiona su propio offline (la cola de
 * fotos).
 *
 * 🔴 Sin esto, `RequestUploadToken` y `createImage` esperaban sin límite: no
 * están en `TIMEOUT_QUEUEABLE_OPERATIONS` y el `RetryLink` reintenta hasta 5
 * veces con el plazo del navegador (300 s en Chromium) por intento. Una sola
 * sin respuesta paraba esa foto, las siguientes de la misma orden (el alta va
 * en serie) y dejaba la pantalla encendida por `withScreenAwake` sin fin.
 *
 * - Al vencer, la promesa falla con `MutationDeadlineError` EN ESE MOMENTO,
 *   aunque el `RetryLink` siguiera esperando para reintentar.
 * - Y se corta la petición de verdad (`fetchOptions.signal`): un plazo que
 *   solo deja de esperar no cancela, la respuesta vuelve tarde y pisa lo que
 *   haya hecho el reintento.
 * - `skipOfflineQueue`: con un fallo de red, el `OfflineQueueLink` la metería
 *   además en la cola genérica de guardados. La cola de fotos ya la conserva y
 *   la reintenta; duplicarla solo mete ruido.
 */
export async function mutateWithDeadline<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  client: MutatingClient,
  options: MutationOptions<TData, TVariables, DefaultContext>,
  timeoutMs: number = UPLOAD_STEP_TIMEOUT_MS,
): Promise<FetchResult<TData>> {
  const operationName = getOperationName(options.mutation) ?? 'mutation';
  if (!isEffectivelyOnline()) throw new BackendUnreachableError(operationName);

  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;

  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      const error = new MutationDeadlineError(operationName, timeoutMs);
      controller.abort(error);
      reject(error);
    }, timeoutMs);
  });

  const request = client.mutate<TData, TVariables>({
    ...options,
    context: {
      ...options.context,
      skipOfflineQueue: true,
      fetchOptions: { ...options.context?.fetchOptions, signal: controller.signal },
    },
  });
  // Si gana el plazo, el rechazo posterior de la petición abortada no debe
  // acabar como «unhandled rejection».
  request.catch(() => undefined);

  try {
    return await Promise.race([request, deadline]);
  } finally {
    clearTimeout(timer);
  }
}
